/**
 * @file     noopAnalyticsService.ts
 * @purpose  Creates a lightweight no-op IAnalyticsService used at boot before the real analytics service is deferred-loaded, preventing null-checks throughout the app.
 */
import type {
  AnalyticsServiceState,
  AnalyticsUserProfile,
  IAnalyticsService,
  PersonalizationState,
} from './analyticsTypes';

const createIsoNow = () => new Date().toISOString();

const emptyIntentScores = {
  buyer: 0,
  seller: 0,
  presale_buyer: 0,
  investor: 0,
  renter: 0,
  general_research: 0,
  unknown: 0,
} as const;

const createEmptyProfile = (): AnalyticsUserProfile => ({
  dominantIntent: 'unknown',
  intentScores: { ...emptyIntentScores },
  intentConfidence: 0,
  lastUpdatedAt: createIsoNow(),
});

const createEmptySession = (): AnalyticsServiceState['session'] => {
  const now = createIsoNow();

  return {
    sessionId: 'noop_session',
    startTime: now,
    lastActiveTime: now,
    lastActivityAt: now,
    activeTimeMs: 0,
    visitedPages: [],
    navigationSequence: [],
    pageVisits: [],
    eventCount: 0,
    events: [],
    scrollMilestones: {},
    dominantIntent: 'unknown',
    intentScores: { ...emptyIntentScores },
    intentConfidence: 0,
    engagementScore: 0,
    leadScore: 0,
    attribution: {
      assistTouches: [],
      conversionPath: [],
      stepsBeforeConversion: 0,
    },
    influencedContent: [],
    behaviorProfile: {
      engagementLevel: 'low',
      dominantInterest: 'unknown',
      primaryJourneyPath: [],
      funnelDepth: 0,
      repeatVisitPatterns: [],
      highIntentSequences: [],
      contentAffinity: {},
    },
    leadProfile: {
      engagementScore: 0,
      leadScore: 0,
      intentConfidence: 0,
      journeyStage: 'awareness',
      classification: 'cold',
    },
    funnelProgress: {},
    utm: {},
  };
};

const createEmptyState = (): AnalyticsServiceState => ({
  session: createEmptySession(),
  profile: createEmptyProfile(),
});

const getNoopPersonalizationState = (
  state: AnalyticsServiceState,
): PersonalizationState => ({
  dominantIntent: state.profile.dominantIntent,
  intentConfidence: state.profile.intentConfidence,
  journeyStage: state.session.leadProfile.journeyStage,
  leadTemperature: state.session.leadProfile.classification,
  engagementLevel: state.session.behaviorProfile.engagementLevel,
  recommendedCtaGroup: 'general',
  emphasizedContentCategory: 'general',
});

export const createNoopAnalyticsService = (): IAnalyticsService => {
  const state = createEmptyState();

  return {
    identify: () => undefined,
    page: () => undefined,
    track: () => undefined,
    trackEvent: () => undefined,
    registerActivity: () => undefined,
    trackScrollMilestone: () => undefined,
    trackSectionView: () => undefined,
    flushPersistedState: () => undefined,
    saveSessionSnapshot: async () => undefined,
    updateUserProfile: async () => undefined,
    flushQueue: async () => ({
      flushedEvents: 0,
      failedEvents: 0,
    }),
    getState: () => state,
    getSessionSnapshot: () => state.session,
    getBehaviorProfile: () => state.session.behaviorProfile,
    getPersonalizationState: () => getNoopPersonalizationState(state),
    subscribe: () => () => undefined,
  };
};
